import { assertUnreachable, call } from '@everlutionsk/helpers';
import { Button as DefaultButton } from '@everlutionsk/ui';
import Add from '@mui/icons-material/Add';
import {
  ButtonProps as MuiButtonProps,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  TypographyTypeMap,
  useTheme
} from '@mui/material';
import React, { ReactNode, useMemo } from 'react';
import { isDarkMode } from '../../theme';

/**
 * Primary UI component for user interaction
 */

const variants = [
  'accent',
  'accentFilled',
  'light',
  'primary',
  'secondary',
  'transparent',
  'destructive',
  'ghost'
] as const;

type VariantSxProps = { styles: SxProps<Theme>; disableButtonStyles: SxProps<Theme> };

function getVariantValues(variant: (typeof variants)[number]): VariantSxProps {
  const isDark = isDarkMode();

  if (variant === 'accent')
    return {
      styles: {
        bgcolor: theme => theme.brandPalette.data.dimAccent,
        color: theme =>
          isDark
            ? theme.brandPalette.background.neutral
            : theme.brandPalette.foreground.neutralOnAccent,
        '&:hover': { bgcolor: theme => theme.brandPalette.data.inverseAccentFaded },
        '&:focus': { bgcolor: theme => theme.brandPalette.data.inverseAccentFaded }
      },
      disableButtonStyles: {
        color: theme => theme.brandPalette.foreground.accent,
        background: 'transparent !important'
      }
    };

  if (variant === 'accentFilled')
    return {
      styles: {
        bgcolor: theme => theme.brandPalette.background.accent,
        color: theme => theme.brandPalette.foreground.inverse,
        '&:hover': { bgcolor: '#274AB3' },
        '&:focus': { bgcolor: '#274AB3' }
      },
      disableButtonStyles: {
        color: theme => theme.brandPalette.foreground.accent,
        background: 'transparent !important'
      }
    };

  if (variant === 'light')
    return {
      styles: {
        bgcolor: '#EFF6FF',
        border: theme => `1px solid ${theme.brandPalette.foreground.accent}`,
        color: theme => theme.brandPalette.foreground.accent,
        '&:hover': { bgcolor: 'transparent' },
        '&:focus': { bgcolor: 'transparent' }
      },
      disableButtonStyles: {
        color: theme => theme.brandPalette.foreground.accent,
        background: 'transparent !important'
      }
    };

  if (variant === 'destructive')
    return {
      styles: {
        bgcolor: theme => theme.brandPalette.background.negative,
        color: theme => theme.brandPalette.foreground.neutralOnAccent,
        '&:hover': { bgcolor: theme => theme.brandPalette.background.negativeHover },
        '&:focus': { bgcolor: theme => theme.brandPalette.background.negativeFocus }
      },
      disableButtonStyles: {
        color: theme => theme.brandPalette.foreground.disabled,
        background: 'transparent !important'
      }
    };

  if (variant === 'ghost')
    return {
      styles: {
        bgcolor: 'transparent',
        color: theme => theme.brandPalette.foreground.neutral,
        '&:hover': { bgcolor: theme => theme.brandPalette.background.secondaryHover },
        '&:focus': { bgcolor: theme => theme.brandPalette.background.secondaryFocus }
      },
      disableButtonStyles: {
        color: theme => theme.brandPalette.foreground.disabled,
        background: 'transparent !important'
      }
    };

  if (variant === 'primary')
    return {
      styles: {
        bgcolor: theme => theme.brandPalette.background.primary,
        color: theme => theme.brandPalette.foreground.inverse,
        '&:hover': { bgcolor: theme => theme.brandPalette.background.primaryHover },
        '&:focus': { bgcolor: theme => theme.brandPalette.background.primaryFocus }
      },
      disableButtonStyles: {
        color: theme => theme.brandPalette.foreground.neutral,
        background: 'transparent !important'
      }
    };

  if (variant === 'secondary')
    return {
      styles: {
        bgcolor: theme => theme.brandPalette.background.secondary,
        color: theme => theme.brandPalette.foreground.neutral,
        '&:hover': { bgcolor: theme => theme.brandPalette.background.secondaryHover },
        '&:focus': { bgcolor: theme => theme.brandPalette.background.secondaryFocus }
      },
      disableButtonStyles: {
        color: theme => theme.brandPalette.foreground.neutralFaded,
        background: 'transparent !important'
      }
    };

  if (variant === 'transparent')
    return {
      styles: {
        bgcolor: 'transparent',
        color: theme => theme.brandPalette.foreground.neutral,
        border: theme => `1px solid ${theme.brandPalette.border.secondary}`,
        '&:hover': { bgcolor: theme => theme.brandPalette.background.secondaryHover },
        '&:focus': { bgcolor: theme => theme.brandPalette.background.secondaryFocus }
      },
      disableButtonStyles: {
        color: theme => theme.brandPalette.foreground.neutralFaded,
        background: 'transparent !important'
      }
    };

  assertUnreachable(variant);
}

const sizes = ['sm', 'base', 'l', 'xl'] as const;

type SizeVariants = Record<
  (typeof sizes)[number],
  {
    textVariant: TypographyTypeMap['props']['variant'];
    styles: SxProps<Theme>;
    disableButtonStyles: SxProps<Theme>;
    iconOnlyStyles: SxProps<Theme>;
  }
>;

const disabledStyles: SxProps<Theme> = {
  bgcolor: theme => theme.brandPalette.background.disabled,
  color: theme => theme.brandPalette.foreground.disabled
};

export interface ButtonProps {
  /**
   * Size of the button
   * */
  size?: (typeof sizes)[number];
  /**
   * Type of button
   * */
  variant?: (typeof variants)[number];
  /**
   * Initials of the button
   * @default ''
   * */
  children?: ReactNode;
  /**
   * Is button disabled
   * @default false
   * */
  disabled?: boolean;
  /**
   * Icon at the beginning of the button
   * */
  startIcon?: ReactNode;
  /**
   * Icon at the end of the button
   * */
  endIcon?: ReactNode;
  /**
   * Text button
   * */
  disableButtonStyles?: boolean;
  /**
   * Icon button
   * */
  onlyIcon?: ReactNode;
  /**
   * Type of button
   * */
  type?: MuiButtonProps['type'];
  /**
   * Form of button
   * */
  form?: MuiButtonProps['form'];
  /**
   * Custom styles
   * @default ''
   * */
  sx?: MuiButtonProps['sx'];
  /**
   * Click handler
   * */
  onClick?: MuiButtonProps['onClick'];
  /**
   * The component used for the root node. Either a string to use a HTML element or a component.
   */
  component?: MuiButtonProps['component'];

  className?: string;
}

export function Button({
  size = 'base',
  variant = 'accent',
  children,
  onlyIcon,
  disableButtonStyles,
  sx,
  ...props
}: ButtonProps) {
  const theme = useTheme();
  const addMode = call((): boolean => {
    if (onlyIcon != null) return false;
    if (children == null) return false;
    if (props.startIcon == null) return false;
    if (!React.isValidElement(props.startIcon)) return false;

    const iconType = props.startIcon.type as {
      readonly displayName?: string;
      readonly name?: string;
    };
    if (props.startIcon.type === Add) return true;
    if (iconType.displayName?.toLowerCase().includes('add') === true) return true;
    if (iconType.name?.toLowerCase().includes('add') === true) return true;
    return false;
  });
  const effectiveOnlyIcon = addMode ? props.startIcon : onlyIcon;
  const effectiveVariant = addMode ? 'accentFilled' : variant;
  const buttonLabel = call((): ReactNode => {
    if (!addMode) return null;
    return children;
  });
  const sizesValues: SizeVariants = {
    sm: {
      textVariant: 'labelXSmall',
      styles: {
        padding: theme => theme.spacing(3, 4),
        '& .MuiButton-startIcon': { marginRight: theme => theme.spacing(1) },
        [theme.breakpoints.up('sm')]: { padding: theme => theme.spacing(3.5, 4.5) },
        [theme.breakpoints.up('md')]: { padding: theme => theme.spacing(4, 5) },
        [theme.breakpoints.up('lg')]: { padding: theme => theme.spacing(4, 6) }
      },
      disableButtonStyles: { padding: 0, minWidth: 'auto' },
      iconOnlyStyles: {
        padding: '6px',
        borderRadius: theme => theme.radius.full,
        minWidth: 'auto',
        '& svg': {
          width: '20px',
          height: '20px',
          [theme.breakpoints.up('md')]: { width: '22px', height: '22px' },
          [theme.breakpoints.up('lg')]: { width: '24px', height: '24px' }
        }
      }
    },
    base: {
      textVariant: 'labelSmall',
      styles: {
        height: '40px',
        padding: theme => theme.spacing(4, 5),
        '& .MuiButton-startIcon': { marginRight: theme => theme.spacing(1) },
        [theme.breakpoints.down('sm')]: { padding: theme => theme.spacing(3, 4) },
        [theme.breakpoints.up('lg')]: { padding: theme => theme.spacing(5, 6) }
      },
      disableButtonStyles: { padding: 0, minWidth: 'auto' },
      iconOnlyStyles: {
        padding: '10px',
        borderRadius: theme => theme.radius.full,
        minWidth: 'auto',
        '& svg': {
          width: '20px',
          height: '20px',
          [theme.breakpoints.up('md')]: { width: '22px', height: '22px' }
        }
      }
    },
    l: {
      textVariant: 'labelSmall',
      styles: {
        padding: theme => theme.spacing(4, 6),
        '& .MuiButton-startIcon': { marginRight: theme => theme.spacing(2) },
        [theme.breakpoints.down('sm')]: { padding: theme => theme.spacing(3.5, 5) },
        [theme.breakpoints.up('lg')]: { padding: theme => theme.spacing(5, 7) }
      },
      disableButtonStyles: { padding: 0, minWidth: 'auto' },
      iconOnlyStyles: {
        padding: '12px',
        borderRadius: theme => theme.radius.full,
        minWidth: 'auto',
        '& svg': {
          width: '24px',
          height: '24px',
          [theme.breakpoints.up('lg')]: { width: '26px', height: '26px' }
        }
      }
    },
    xl: {
      textVariant: 'labelSmall',
      styles: {
        padding: theme => theme.spacing(5, 7),
        '& .MuiButton-startIcon': { marginRight: theme => theme.spacing(2) },
        [theme.breakpoints.down('sm')]: { padding: theme => theme.spacing(4, 6) },
        [theme.breakpoints.up('xl')]: { padding: theme => theme.spacing(6, 8) }
      },
      disableButtonStyles: { padding: 0, minWidth: 'auto' },
      iconOnlyStyles: {
        padding: '16px',
        borderRadius: theme => theme.radius.full,
        minWidth: 'auto',
        '& svg': {
          width: '24px',
          height: '24px',
          [theme.breakpoints.up('xl')]: { width: '28px', height: '28px' }
        }
      }
    }
  };
  const sizeStyles = call(() => {
    if (effectiveOnlyIcon != null) return sizesValues[size].iconOnlyStyles;
    if (disableButtonStyles) return sizesValues[size].disableButtonStyles;

    return sizesValues[size].styles;
  });

  const variants = getVariantValues(effectiveVariant);

  const variantStyles: SxProps<Theme> = disableButtonStyles
    ? variants.disableButtonStyles
    : variants.styles;

  const isDisabled: SxProps<Theme> = call(() => {
    if (props.disabled && variant !== 'ghost') return disabledStyles;

    return {};
  });

  const borderRadius: SxProps<Theme> = { borderRadius: theme => theme.radius.full };

  const mergedStyles: SxProps<Theme> = useMemo<SxProps<Theme>>(
    () => ({
      ...Object.assign({}, variantStyles, sizeStyles, isDisabled, borderRadius, sx)
    }),
    [variantStyles, sizeStyles, isDisabled, sx, borderRadius]
  );

  const button = (
    <DefaultButton
      className={props.className}
      disableRipple
      disableElevation
      aria-modal
      sx={theme => ({
        ...mergedStyles,
        [theme.breakpoints.down('sm')]: {
          width: addMode ? 'auto' : '100%'
        }
      })}
      {...props}
      startIcon={addMode ? undefined : props.startIcon}
      endIcon={addMode ? undefined : props.endIcon}
    >
      {call((): ReactNode => {
        if (effectiveOnlyIcon != null) return effectiveOnlyIcon;
        if (children == null) return null;
        if (addMode) return null;

        return (
          <Typography
            variant={sizesValues[size].textVariant}
            maxWidth="100%"
            sx={{
              [theme.breakpoints.down('sm')]: {
                fontSize: theme => theme.typography.subheading2XSmall.fontSize
              },
              [theme.breakpoints.up('md')]: {
                fontSize: theme => theme.typography.subheadingXSmall.fontSize
              },
              [theme.breakpoints.up('lg')]: {
                fontSize: theme => theme.typography.labelSmall.fontSize
              }
            }}
          >
            {children}
          </Typography>
        );
      })}
    </DefaultButton>
  );

  if (!addMode) return button;
  return (
    <Tooltip title={buttonLabel} arrow>
      <span>{button}</span>
    </Tooltip>
  );
}
